import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { createClient } from '@connectrpc/connect'
import { useTransport } from '@connectrpc/connect-query'
import {
  BiddingBlockService,
  CheckBiddingBlockSubmissionDuplicatesRequestSchema,
} from '@ebalance/proto/bffuser/v1/bidding_block_service_pb'
import {
  BiddingSpotService,
  CheckBiddingSpotSubmissionDuplicatesRequestSchema,
} from '@ebalance/proto/bffuser/v1/bidding_spot_service_pb'

import type { SubmissionDuplicateCheck } from '~/components/bidding/common/SubmitConfirmModal'

export type SpotSubmissionDuplicateTarget = {
  id: string
  expectedUpdatedAt: Timestamp | undefined
  spotBidIds: readonly string[]
}

export type BlockSubmissionDuplicateTarget = {
  id: string
  expectedUpdatedAt: Timestamp | undefined
}

/**
 * 確認モーダルを開くたびに重複件数を取得する。React Query cache は使わず、閉じる・
 * 対象変更・再表示時に前回の件数を確定結果として出さない。最新の呼び出しだけを採用する。
 */
function useSubmissionDuplicateCheck(
  enabled: boolean,
  hasTargets: boolean,
  read: () => Promise<SubmissionDuplicateCheck>,
  refreshKey: string,
  sourceFetching: boolean,
): SubmissionDuplicateCheck | undefined {
  const [reloadVersion, setReloadVersion] = useState(0)
  const [result, setResult] = useState<{
    read: typeof read
    refreshKey: string
    reloadVersion: number
    value: SubmissionDuplicateCheck
  }>()
  const requestSequence = useRef(0)
  const retry = useCallback(() => {
    setReloadVersion((version) => version + 1)
  }, [])

  useEffect(() => {
    if (!enabled || !hasTargets || sourceFetching) {
      setResult(undefined)
      return
    }
    const sequence = ++requestSequence.current
    const save = (value: SubmissionDuplicateCheck): void => {
      if (requestSequence.current === sequence) {
        setResult({ read, refreshKey, reloadVersion, value })
      }
    }
    save({ kind: 'checking' })
    void read()
      .then(save)
      .catch(() => {
        save({ kind: 'failed', onRetry: retry })
      })
    return () => {
      requestSequence.current += 1
    }
  }, [
    enabled,
    hasTargets,
    read,
    refreshKey,
    reloadVersion,
    retry,
    sourceFetching,
  ])

  if (!enabled || !hasTargets) return undefined
  if (
    sourceFetching ||
    result?.read !== read ||
    result.refreshKey !== refreshKey ||
    result.reloadVersion !== reloadVersion
  )
    return { kind: 'checking' }
  return result.value
}

export function useSpotSubmissionDuplicateCheck(
  enabled: boolean,
  targets: readonly SpotSubmissionDuplicateTarget[],
  refreshKey = '',
  sourceFetching = false,
): SubmissionDuplicateCheck | undefined {
  const transport = useTransport()
  const read = useMemo(() => {
    const request = create(CheckBiddingSpotSubmissionDuplicatesRequestSchema, {
      targets: targets.map((target) => ({
        ...target,
        spotBidIds: [...target.spotBidIds],
      })),
    })
    return async (): Promise<SubmissionDuplicateCheck> => {
      const response = await createClient(
        BiddingSpotService,
        transport,
      ).checkBiddingSpotSubmissionDuplicates(request)
      return {
        bidKind: 'spot',
        count: response.duplicateCount,
        kind: 'duplicates',
      }
    }
  }, [targets, transport])
  return useSubmissionDuplicateCheck(
    enabled,
    targets.length > 0,
    read,
    refreshKey,
    sourceFetching,
  )
}

export function useBlockSubmissionDuplicateCheck(
  enabled: boolean,
  targets: readonly BlockSubmissionDuplicateTarget[],
  refreshKey = '',
  sourceFetching = false,
): SubmissionDuplicateCheck | undefined {
  const transport = useTransport()
  const read = useMemo(() => {
    const request = create(CheckBiddingBlockSubmissionDuplicatesRequestSchema, {
      targets: [...targets],
    })
    return async (): Promise<SubmissionDuplicateCheck> => {
      const response = await createClient(
        BiddingBlockService,
        transport,
      ).checkBiddingBlockSubmissionDuplicates(request)
      return {
        bidKind: 'block',
        count: response.duplicateGroupCount,
        kind: 'duplicates',
      }
    }
  }, [targets, transport])
  return useSubmissionDuplicateCheck(
    enabled,
    targets.length > 0,
    read,
    refreshKey,
    sourceFetching,
  )
}
